<template>
    <div class="dispatch-parameters">
        <div class="content-block dx-card responsive-paddings">
            <div class="params-header">
                <h5>Parameters</h5>
                <DxButton text="Clear" icon="clear" styling-mode="text" @click="clearAll" />
            </div>
            <!-- <p class="muted">Define what this dispatch is for. Operator and Area are optional.</p> -->

            <div class="field">
                <h6>Dispatch Name <span class="req">*</span> :</h6>
                <DxTextBox v-model:value="form.name" placeholder="e.g. North Field Methanol Run" />
            </div>

            <div class="field">
                <h6>Dispatch Date <span class="req">*</span> :</h6>
                <DxDateBox v-model:value="form.dispatchDate" type="date"
                    placeholder="Select a date" :show-clear-button="true" />
            </div>

            <div class="field">
                <h6>Delivery Method <span class="req">*</span> :</h6>
                <DxSelectBox :data-source="deliveryMethods" value-expr="value" display-expr="text"
                    v-model:value="form.deliveryMethod" />
                <p class="hint">
                    <template v-if="form.deliveryMethod === 'totes'">
                        Product travels in IBC totes on a deck truck. The Supply step asks
                        for each product's tote size and how many you have.
                    </template>
                    <template v-else>
                        Product is pumped from a single-shell tanker, so a truck carries one
                        product per run. The Supply step asks how much bulk you have.
                    </template>
                </p>
            </div>

            <div class="field">
                <h6>Product <span class="req">*</span> <span class="opt">(multi-select)</span> :</h6>
                <DxTagBox :data-source="products" v-model:value="form.product"
                    placeholder="Select one or more products" :search-enabled="true"
                    :show-selection-controls="true" :show-clear-button="true"
                    :max-displayed-tags="3" :show-multi-tag-only="true" />
                <p v-if="form.product.length > 1" class="hint">
                    A truck can only carry one product per compartment. Delivery planning
                    will split these {{ form.product.length }} products across compartments,
                    which usually needs more trucks or more trips than a single-product run.
                </p>
            </div>

            <div class="field">
                <h6>Operator <span class="opt">(optional, multi-select)</span> :</h6>
                <DxTagBox :data-source="operators" value-expr="operatorName"
                    display-expr="operatorName" v-model:value="form.operator"
                    placeholder="Select one or more operators" :search-enabled="true"
                    :show-selection-controls="true" :show-clear-button="true"
                    :max-displayed-tags="3" :show-multi-tag-only="true"
                    @value-changed="onOperatorChanged" />
            </div>

            <div class="field">
                <h6>Area <span class="opt">(optional, multi-select)</span>
                    <span v-if="form.operator.length" class="opt">&mdash; limited to areas
                        {{ form.operator.length === 1 ? 'this operator works' : 'these operators work' }}</span>
                </h6>
                <DxTagBox :data-source="areas" value-expr="areaName" display-expr="areaName"
                    v-model:value="form.area" placeholder="Select one or more areas"
                    :search-enabled="true" :show-selection-controls="true"
                    :show-clear-button="true" :max-displayed-tags="3"
                    :show-multi-tag-only="true" />
            </div>

            <div class="field">
                <h6>Maximum DTE <span class="opt">(days to empty)</span> :</h6>
                <DxNumberBox v-model:value="form.maxDte" :min="0" :show-spin-buttons="true"
                    placeholder="Maximum days to empty" />
            </div>

            <div class="field">
                <h6>Minimum Ullage <span class="opt">(litres)</span> :</h6>
                <DxNumberBox v-model:value="form.minUllage" :min="0" :show-spin-buttons="true"
                    placeholder="Minimum volume in litres" />
            </div>

            <div class="field">
                <h6>Maximum Fill <span class="opt">(%)</span> :</h6>
                <DxNumberBox v-model:value="form.maxFillPct" :min="1" :max="100"
                    :show-spin-buttons="true" placeholder="Maximum fill percentage" />
            </div>

            <div v-if="error" class="error-msg">{{ error }}</div>
        </div>

        <div class="nav-row">
            <DxButton icon="chevronleft" text="Previous" styling-mode="outlined" @click="onPrevious" />
            <DxButton icon="chevronright" text="Next" type="default" styling-mode="contained"
                @click="onNext" />
        </div>
    </div>
</template>

<script>
import DxSelectBox from 'devextreme-vue/select-box';
import DxTagBox from 'devextreme-vue/tag-box';
import DxTextBox from 'devextreme-vue/text-box';
import DxNumberBox from 'devextreme-vue/number-box';
import DxDateBox from 'devextreme-vue/date-box';
import { DxButton } from 'devextreme-vue/button';

export default {
    name: 'DispatchParameters',
    components: {
        DxSelectBox,
        DxTagBox,
        DxTextBox,
        DxNumberBox,
        DxDateBox,
        DxButton,
    },
    emits: ['previous', 'next'],
    data() {
        return {
            error: null,
            deliveryMethods: [
                { value: 'tanker', text: 'Tanker (bulk)' },
                { value: 'totes', text: 'Totes (deck truck)' },
            ],
            // Set once the form has been seeded from the store, so hydrating a saved
            // dispatch doesn't look like the user changing the operator.
            hydrated: false,
            form: {
                name: '',
                dispatchDate: null,
                deliveryMethod: 'tanker',
                product: [],
                operator: [],
                area: [],
                maxDte: null,
                minUllage: null,
                maxFillPct: 95,
            },
        };
    },
    computed: {
        products() {
            return this.$store.getters['dispatch/getProducts'];
        },
        operators() {
            return this.$store.getters['operators/getOperators'];
        },
        // Which areas each operator works. Loaded by the builder.
        operatorAreas() {
            return this.$store.getters['operators/getOperatorsAreas'] || [];
        },
        // Every area, or — once operators are chosen — only the ones those
        // operators actually work, so the picker can't offer a pairing that would
        // match no sites.
        areas() {
            const all = this.$store.getters['areas/getAreas'] || [];
            if (!this.form.operator.length) return all;

            const allowed = new Set(
                this.operatorAreas
                    .filter((x) => this.form.operator.includes(x.operatorName))
                    .map((x) => x.areaName)
            );
            // Keep anything already selected in the list as well: a dispatch saved
            // before the mapping changed should still show the area it stored
            // rather than silently losing it from the box.
            this.form.area.forEach((name) => allowed.add(name));

            return all.filter((a) => allowed.has(a.areaName));
        },
        currentDispatch() {
            return this.$store.getters['dispatch/getCurrentDispatch'];
        },
    },
    created() {
        // Hydrate the form from the current dispatch (new or loaded). Operator
        // and Area are multi-select arrays; loaded/recent dispatches may store
        // them as a single string, so normalize to an array.
        const cd = this.currentDispatch;
        this.form.name = cd.name || '';
        this.form.dispatchDate = cd.parameters.dispatchDate ?? null;
        this.form.deliveryMethod = cd.parameters.deliveryMethod === 'totes' ? 'totes' : 'tanker';
        // Older dispatches stored a single product string, and an "all products"
        // dispatch stored no product at all; both normalise to the array form.
        this.form.product = this.toArray(cd.parameters.product);
        this.form.operator = this.toArray(cd.parameters.operator);
        this.form.area = this.toArray(cd.parameters.area);
        this.form.maxDte = cd.parameters.maxDte ?? null;
        this.form.minUllage = cd.parameters.minUllage ?? null;
        this.form.maxFillPct = cd.parameters.maxFillPct ?? 95;
        this.$nextTick(() => {
            this.hydrated = true;
        });
    },
    methods: {
        // Areas belong to operators, so any area picked under a previous operator
        // selection is no longer necessarily valid — clear it and let the user
        // choose again from the narrowed list.
        onOperatorChanged() {
            if (!this.hydrated) return;
            this.form.area = [];
        },
        toArray(value) {
            if (Array.isArray(value)) return value;
            return value ? [value] : [];
        },
        clearAll() {
            this.form.name = '';
            this.form.dispatchDate = null;
            this.form.deliveryMethod = 'tanker';
            this.form.product = [];
            this.form.operator = [];
            this.form.area = [];
            this.form.maxDte = null;
            this.form.minUllage = null;
            this.form.maxFillPct = 95;
            this.error = null;
        },
        persist() {
            // Write name (dispatch root) and parameters in one commit.
            const cd = this.currentDispatch;
            this.$store.commit('dispatch/setCurrentDispatch', {
                ...cd,
                name: this.form.name,
                parameters: {
                    dispatchDate: this.form.dispatchDate,
                    deliveryMethod: this.form.deliveryMethod,
                    product: this.form.product,
                    operator: this.form.operator,
                    area: this.form.area,
                    maxDte: this.form.maxDte,
                    minUllage: this.form.minUllage,
                    maxFillPct: this.form.maxFillPct,
                },
            });
        },
        onPrevious() {
            this.persist();
            this.$emit('previous');
        },
        onNext() {
            if (!this.form.name || !this.form.name.trim()) {
                this.error = 'Dispatch Name is required.';
                return;
            }
            if (!this.form.product.length) {
                this.error = 'Select at least one product.';
                return;
            }
            this.error = null;
            this.persist();
            this.$emit('next');
        },
    },
};
</script>

<style scoped>
.dispatch-parameters {
    max-width: 700px;
    margin: 0 auto;
}

.params-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

h5 {
    margin-top: 0;
    margin-bottom: 0;
    font-family: Roboto, Verdana, Geneva, Tahoma, sans-serif;
    font-weight: 300;
}

.field {
    margin-bottom: 6px;
}

h6 {
    margin: 22px 0 6px 0;
    font-size: 14px;
    font-weight: lighter;
}

.req {
    color: rgb(255, 115, 0);
}

.hint {
    margin: 8px 0 0 0;
    font-size: 12px;
    line-height: 1.5;
    color: rgba(150, 150, 150, 0.9);
}

.hint {
    margin: 8px 0 0 0;
    font-size: 12px;
    line-height: 1.5;
    color: rgb(255, 140, 40);
}

.opt {
    color: rgba(150, 150, 150, 0.9);
    font-size: 12px;
}

.muted {
    color: rgba(150, 150, 150, 0.9);
    font-size: 13px;
}

.error-msg {
    margin-top: 16px;
    color: #d9534f;
    font-size: 13px;
}

.nav-row {
    display: flex;
    justify-content: space-between;
    margin: 20px;
}
</style>
